import type { CopyLinkOutcome, NativeShareOutcome } from "./shareFlow";

/**
 * The exact disclosure copy the round-5 spec requires: the player's own
 * public display-name snapshot and the verified target their friend will
 * be able to see, spelled out BEFORE any share/copy action - never shown
 * only after the fact. Pure and framework-free so the copy itself (not
 * just that "a name and target are shown somewhere") is unit-testable.
 */
export function buildShareDisclosure(creatorDisplayName: string, targetDailyPoints: number): string {
  return `Your public name ${creatorDisplayName} and target of ${targetDailyPoints.toLocaleString("en-US")} Daily Points will be visible to anyone with this link.`;
}

export type ShareModalFeedback =
  | { kind: "none" }
  | { kind: "copied" }
  | { kind: "copy_failed"; url: string }
  | { kind: "share_failed" }
  | { kind: "cancelled" };

/**
 * Maps a native-share outcome to what the modal shows next. A successful
 * share and a cancellation both resolve to visible, non-alarming feedback
 * (never silence, and a cancellation must never look like a success); a
 * genuine failure keeps COPY LINK front and center as the retry path -
 * see the round-5 spec's "share failure returns to the modal with a
 * useful retry/copy option."
 */
export function feedbackForNativeShareOutcome(outcome: NativeShareOutcome, _url: string): ShareModalFeedback {
  switch (outcome) {
    case "shared":
      return { kind: "none" };
    case "cancelled":
      return { kind: "cancelled" };
    case "failed":
    case "unsupported":
      return { kind: "share_failed" };
  }
}

/** Maps a copy outcome to modal feedback - success shows the confirmation copy, failure surfaces the URL itself as a selectable fallback (never just a generic error with no way to recover the link). */
export function feedbackForCopyOutcome(outcome: CopyLinkOutcome, url: string): ShareModalFeedback {
  return outcome === "copied" ? { kind: "copied" } : { kind: "copy_failed", url };
}
